<template lang="pug">
.schedule-page.flex.column
  calendar-view(
    ref="calendar"
    :professional-id="professionalId"
    :loader="loadCalendar"
    :empty-text="professionalId ? '' : '請先選擇專業人士'"
    :availability="availability"
    :holidays="holidays"
    @weekChange="loadHolidays"
    @showPatient="showPatient"
    @showDetail="showDetail"
    @showActivity="showActivity")
    template(slot="filter")
      pro-selector.pro-input(v-model="professionalId", public-only, use-group)
  el-dialog(
    width="600px"
    title="預約詳細記錄"
    :visible.sync="detailVisible"
  )
    appointment-detail(
      @edit="execCommand('edit', $event)"
      @recreate="execCommand('recreate', $event)"
      @cancel="execCommand('cancel', $event)"
      :tags="tags"
      :data="detailAppointment"
      :orgMap="projectOrgMap")
  appointment-edit(
    ref="appointmentEdit"
    @finish="reloadDataWithDelay"
  )
  appointment-creator(
    ref="appointmentCreator"
    @finish="reloadDataWithDelay"
  )
  activity-editor(
    ref="activityEditor"
    @finish="reloadDataWithDelay"
  )
  person-viewer(
    ref="personViewer"
  )
</template>

<script>
import { getTags } from '@/api/tags';
import { getOrganizations } from '@/api/project';
import { cancelAppointment, getAppointment } from '@/api/reserve';
import { getHolidaysForDates } from '@/api/holiday';
import { getProfessionalCalendar, getProfessionalAvailability } from '@/api/professionals';
import CalendarView from '@/components/AppointmentCalendarView';
import AppointmentDetail from '@/components/AppointmentDetail.vue';
import AppointmentEditor from '@/components/drawers/AppointmentEdit.vue';
import AppointmentCreate from '@/components/drawers/AppointmentCreate.vue';
import ActivityEditor from '@/components/drawers/ActivityEditor.vue';
import PersonViewer from '@/components/drawers/PersonViewer.vue';

export default {
  name: 'SchedulePage',
  components: {
    'calendar-view': CalendarView,
    'appointment-detail': AppointmentDetail,
    'appointment-edit': AppointmentEditor,
    'appointment-creator': AppointmentCreate,
    'activity-editor': ActivityEditor,
    'person-viewer': PersonViewer,
  },
  data() {
    return {
      professionalId: undefined,
      availability: null,
      holidays: [],

      tags: [],
      projectOrgMap: {},

      detailVisible: false,
      detailAppointment: undefined,
    };
  },
  watch: {
    professionalId() {
      this.loadAvailability();
    },
  },
  methods: {
    // 失敗時靜默視為無假日；快速切週時只採用最後一次請求的結果
    async loadHolidays(dates) {
      this.holidaySeq = (this.holidaySeq || 0) + 1;
      const seq = this.holidaySeq;
      let holidays = [];
      try {
        holidays = await getHolidaysForDates(dates.map((d) => d.value));
      } catch (e) {
        holidays = [];
      }
      if (seq === this.holidaySeq) {
        this.holidays = holidays;
      }
    },
    // 失敗時靜默不畫標示，行事曆仍可使用
    async loadAvailability() {
      const id = this.professionalId;
      this.availability = null;
      if (!id) {
        return;
      }
      try {
        const data = await getProfessionalAvailability(id);
        // 載入期間又換了專業人士則丟棄
        if (id === this.professionalId) {
          this.availability = data;
        }
      } catch (e) {
        this.availability = null;
      }
    },
    // 個館看其他館別的預約只會拿到時段 (occupied)，沒權限的活動只有名稱與時段
    loadCalendar(filter) {
      return getProfessionalCalendar(filter.professional_id, filter.start_at, filter.end_at);
    },
    reloadDataWithDelay() {
      setTimeout(() => {
        this.$refs.calendar.loadData();
      }, 100);
    },
    execCommand(cmd, data) {
      if (cmd === 'edit') {
        this.detailVisible = false;
        this.$refs.appointmentEdit.$emit('show', data);
      } else if (cmd === 'cancel') {
        this.detailVisible = false;
        this.cancelAppointment(data);
      } else if (cmd === 'recreate') {
        this.detailVisible = false;
        this.$refs.appointmentCreator.$emit('show', data);
      }
    },
    async cancelAppointment(appointment) {
      this.$confirm('確定要取消此預約嗎').then(
        () => this.$execWithLoading(async () => {
          await cancelAppointment(appointment.id);
          this.$showSuccess('已取消預約');
          this.reloadDataWithDelay();
        }, (e) => this.$showAxiosException('取消預約失敗', e)),
      ).catch(() => {});
    },
    showDetail(current) {
      this.$execWithLoading(async () => {
        this.detailAppointment = await getAppointment(current.id);
        this.detailVisible = true;
      }, (e) => {
        this.$showAxiosException('載入資料失敗', e);
      });
    },
    showPatient(id) {
      this.$refs.personViewer.$emit('show', {
        type: 'id',
        data: id,
      });
    },
    showActivity(data) {
      // 個館帳號取得的活動不含 id，無法開啟編輯
      if (!data?.id) {
        return;
      }
      this.$refs.activityEditor.$emit('show', data);
    },
    async loadTags() {
      this.tags = await getTags();
    },
    async loadOrganizations() {
      const data = await getOrganizations();
      const projectOrgMap = {};
      data.forEach((d) => {
        d.projects.forEach((p) => {
          projectOrgMap[p.id] = d;
        });
      });
      this.projectOrgMap = projectOrgMap;
    },
  },
  mounted() {
    this.$execWithLoading(async () => {
      await Promise.all([
        this.loadTags(),
        this.loadOrganizations(),
      ]);
    }, (e) => this.$showAxiosException('讀取標籤與單位失敗', e));
  },
};
</script>

<style lang="scss" scoped>
.schedule-page {
  position: relative;
  padding: 30px;
  .pro-input {
    width: 240px;
  }
}
</style>
